/**
 * The question types a recipe module's legal shapes take (draft guard), its
 * own declared type first.
 *
 * Projected from the Python recipe registry (`RecipeModule.legal_shapes`, the
 * one owner, which the plan check reads with each shape's conditions) into
 * `config/questionnaire-recipe-registry.json` as `moduleShapeTypes`.
 *
 * Cache scope: one-shot, not cached.
 */

import recipeManifest from "../../../../config/questionnaire-recipe-registry.json";
import type { QuestionType } from "./index";
import { studySpecOf } from "./study-spec";
import type { StudySpecRecipe } from "./study-spec.generated";

interface RecipeManifestEntry {
  latestVersion: string;
  versions: Record<
    string,
    {
      moduleShapeTypes?: Record<string, QuestionType[]>;
    }
  >;
}

const RECIPES = (
  recipeManifest as unknown as {
    recipes: Record<string, RecipeManifestEntry>;
  }
).recipes;

export function recipeModuleShapeTypes(
  recipe: StudySpecRecipe,
  moduleId: string,
): readonly QuestionType[] | null {
  const entry = RECIPES[recipe.recipeId];
  if (!entry) {
    return null;
  }
  const version =
    entry.versions[recipe.recipeVersion] ??
    entry.versions[entry.latestVersion];
  return version?.moduleShapeTypes?.[moduleId] ?? null;
}

/**
 * The recipe module a question claims (`planAttribution.recipeModuleId`) and
 * the type it declares, when the question's type disagrees with it: the one
 * answer the validator refuses (`question_recipe_module_type_mismatch`) and
 * a `change_type` leaves the module on (`plan_attribution_cleared`). Null
 * when the question claims no module, the draft records no recipe, or the
 * recipe declares no shapes for that module.
 */
export function recipeModuleTypeMismatchOf(
  draft: unknown,
  question: { planAttribution?: unknown; type?: unknown },
): { moduleId: string; expected: QuestionType; actual: string } | null {
  const attribution = question.planAttribution;
  if (typeof attribution !== "object" || attribution === null) return null;
  const moduleId = (attribution as { recipeModuleId?: unknown }).recipeModuleId;
  if (typeof moduleId !== "string" || moduleId.length === 0) return null;
  const recipe = studySpecOf(draft as Parameters<typeof studySpecOf>[0])?.recorded?.recipe;
  if (!recipe) return null;
  const shapeTypes = recipeModuleShapeTypes(recipe, moduleId);
  if (!shapeTypes || shapeTypes.length === 0) return null;
  const actual = question.type;
  if (typeof actual !== "string") return null;
  return (shapeTypes as readonly string[]).includes(actual)
    ? null
    : { moduleId, expected: shapeTypes[0], actual };
}
